import { ref } from "vue";
import { defineStore } from "pinia";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { convertToInt } from "@/composables/util";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";

export const useLoadRatingStore = defineStore("loadRating", () => {
  //loadRating data is loaded either from idb(pouch db for inspection module) or service(for management module)
  const configStore = useConfigStore();
  let loadRatingData = ref({});
  let calculationDates = ref([]);

  const loadLoadRatingObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      //if moduleType is management, load data from service call
      try {
        await getInspLinkLoadRating(brkey, inspectionId);
      } catch (e) {
        LOGGER.logException(e);
      }
    }
  };

  const getLoadRatingObject = () => {
    return clone(loadRatingData.value);
  };

  const getCalculationDates = () => {
    return clone(calculationDates.value);
  };

  const setCalculationDates = (value) => {
    calculationDates.value = value;
  };

  const saveLoadRating = async (moduleType, loadRatingValue, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(loadRatingValue, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateLoadRating(convertToSaveInspLinkObject(loadRatingValue));
    }
    return isSuccess;
  };

  const getInspLinkLoadRating = async (brkey, inspectionId) => {
    return new Promise((resolve, reject) => {
      loadRatingData.value = {};
      let url = INSPECTION_LINKS_ENDPOINT.GET_LOAD_RATING_AND_POSTING.replace("{brkey}", brkey).replace(
        "{inspectionId}",
        inspectionId,
      );
      axios
        .get(url)
        .then((response) => {
          convertToInspLinkObject(response.data.data);
          resolve(true);
        })
        .catch((e) => {
          reject(new Error(e));
        });
    });
  };
  const updateLoadRating = async (loadRating) => {
    let isSuccess = true;
    await axios
      .put(INSPECTION_LINKS_ENDPOINT.UPDATE_LOAD_RATING_AND_POSTING, loadRating)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update LoadRating", response);
        convertToInspLinkObject(response.data.data);
        isSuccess = true;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspLinkObject = (loadRating) => {
    let postings = [];
    for (const tPosting of loadRating?.postings ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of tPosting?.VEH_POST_TYPES ?? []) {
        vehPostTypes.push({
          postingId: vehPostType.POSTING_ID,
          vehPostType: vehPostType.VEH_POST_TYPE,
        });
      }
      postings.push({
        postingId: tPosting.POSTING_ID,
        brkey: tPosting.BRKEY,
        postingKey: tPosting.POSTING_KEY,
        active: tPosting.ACTIVE,
        fieldCond: tPosting.FIELD_COND,
        impact: tPosting.IMPACT,
        permanentlyClosedStruc: tPosting.PERMANENTLY_CLOSED_STRUC,
        postLimitComb: tPosting.POST_LIMIT_COMB,
        postLimitWeight: tPosting.POST_LIMIT_WEIGHT,
        postReason: tPosting.POST_REASON,
        postStatus: tPosting.POST_STATUS,
        postStatusDate: tPosting.POST_STATUS_DATE,
        snbiPostCondition: tPosting.SNBI_POST_CONDITION,
        snbiPostStatus: tPosting.SNBI_POST_STATUS,
        specCond: tPosting.SPEC_COND,
        specRestrictPost: tPosting.SPEC_RESTRICT_POST,
        updateDateTime: tPosting.MODTIME,
        updateUserId: tPosting.USERKEY,
        vehPostTypes: vehPostTypes,
      });
    }
    let ratingLoads = [];
    for (const tRatingLoad of loadRating?.ratingLoads ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of tRatingLoad?.VEH_POST_TYPES ?? []) {
        vehPostTypes.push({
          ratingLoadId: vehPostType.RATING_LOAD_ID,
          vehPostType: vehPostType.VEH_POST_TYPE,
        });
      }
      ratingLoads.push({
        ratingLoadId: tRatingLoad.RATING_LOAD_ID,
        brkey: tRatingLoad.BRKEY,
        ratingDate: tRatingLoad.RATING_DATE,
        ratingKey: tRatingLoad.RATING_KEY,
        aashtoManualYear: tRatingLoad.AASHTO_MANUAL_YEAR,
        aashtoSpecYear: tRatingLoad.AASHTO_SPEC_YEAR,
        analysisEngineer: tRatingLoad.ANALYSIS_ENGINEER,
        controlMemType: tRatingLoad.CONTROL_MEM_TYPE,
        createdDateTime: tRatingLoad.CREATEDATETIME,
        createdUserId: tRatingLoad.CREATEUSERKEY,
        fatigLoadType: tRatingLoad.FATIG_LOAD_TYPE,
        fatigStressCat: tRatingLoad.FATIG_STRESS_CAT,
        invRatingStressMethod: tRatingLoad.INV_RATING_STRESS_METHOD,
        irLoad: tRatingLoad.IRLOAD,
        irLoadFactor: tRatingLoad.IR_LOAD_FACTOR,
        legalLoadRF: tRatingLoad.LEGAL_LOAD_RF,
        loadType: tRatingLoad.LOAD_TYPE,
        nbiRatingInd: tRatingLoad.NBI_RATING_IND,
        notes: tRatingLoad.NOTES,
        oprRatingStressMethod: tRatingLoad.OPR_RATING_STRESS_METHOD,
        orLoad: tRatingLoad.ORLOAD,
        orLoadFactor: tRatingLoad.OR_LOAD_FACTOR,
        ratingAnalysisMethod: tRatingLoad.RATING_ANALYSIS_METHOD,
        slcRating: tRatingLoad.SLC_RATING,
        stressRange: tRatingLoad.STRESS_RANGE,
        supportDataset: tRatingLoad.SUPPORT_DATASET,
        updateDateTime: tRatingLoad.MODTIME,
        updateUserId: tRatingLoad.USERKEY,
        vehPostWeight: tRatingLoad.VEH_POST_WEIGHT,
        vehPostTypes: vehPostTypes,
        deleted: tRatingLoad.deleted,
      });
    }

    return {
      brkey: loadRating.BRKEY,
      inspectionId: loadRating.INSPECTIONID,
      recommendRatingReview: loadRating.RECOMMEND_RATING_REVIEW,
      lrReviewAction: loadRating.LR_REVIEW_ACTION,
      postReviewRec: loadRating.POST_REVIEW_REC,
      calculationDate: loadRating.RATING_DATE,
      ratingApprDate: loadRating.RATING_APPR_DATE,
      approverName: loadRating.APPROVER_NAME,
      designLoad: loadRating.DESIGNLOAD,
      servTypOn: loadRating.SERVTYPON,
      designMethod: loadRating.DESIGN_METHOD,
      routinePermitLoads: loadRating.ROUTINE_PERMIT_LOADS,
      postings: postings,
      ratingLoads: ratingLoads,
      tunnelLoadRating: loadRating.tunnelLoadRating,
      calculationDates: calculationDates.value,
      assignedRatingDates: loadRating.assignedRatingDates,
      inspectionComments: loadRating.inspectionComments,
      nbiAndAlternateRating: loadRating.nbiAndAlternateRating,
      previousInspectionComments: loadRating.previousInspectionComments,
    };
  };

  const convertToSaveInspModuleObject = (loadRatingValue, structure) => {
    let postings = [];
    for (const tPosting of loadRatingValue?.postings ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of tPosting?.VEH_POST_TYPES ?? []) {
        vehPostTypes.push({
          postingId: vehPostType.POSTING_ID,
          vehPostType: vehPostType.VEH_POST_TYPE,
        });
      }
      postings.push({
        postingId: tPosting.POSTING_ID,
        brkey: tPosting.BRKEY,
        postingKey: tPosting.POSTING_KEY,
        active: tPosting.ACTIVE,
        fieldCond: tPosting.FIELD_COND,
        impact: tPosting.IMPACT,
        permanentlyClosedStruc: tPosting.PERMANENTLY_CLOSED_STRUC,
        postLimitComb: tPosting.POST_LIMIT_COMB,
        postLimitWeight: tPosting.POST_LIMIT_WEIGHT,
        postReason: tPosting.POST_REASON,
        postStatus: tPosting.POST_STATUS,
        postStatusDate: tPosting.POST_STATUS_DATE,
        snbiPostCondition: tPosting.SNBI_POST_CONDITION,
        snbiPostStatus: tPosting.SNBI_POST_STATUS,
        specCond: tPosting.SPEC_COND,
        specRestrictPost: tPosting.SPEC_RESTRICT_POST,
        updateDateTime: tPosting.MODTIME,
        updateUserId: tPosting.USERKEY,
        vehPostTypes: vehPostTypes,
      });
    }
    let ratingLoads = [];
    for (const tRatingLoad of loadRatingValue?.ratingLoads ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of tRatingLoad?.VEH_POST_TYPES ?? []) {
        vehPostTypes.push({
          ratingLoadId: vehPostType.RATING_LOAD_ID,
          vehPostType: vehPostType.VEH_POST_TYPE,
        });
      }
      ratingLoads.push({
        ratingLoadId: tRatingLoad.RATING_LOAD_ID,
        brkey: tRatingLoad.BRKEY,
        ratingDate: tRatingLoad.RATING_DATE,
        ratingKey: tRatingLoad.RATING_KEY,
        aashtoManualYear: tRatingLoad.AASHTO_MANUAL_YEAR,
        aashtoSpecYear: tRatingLoad.AASHTO_SPEC_YEAR,
        analysisEngineer: tRatingLoad.ANALYSIS_ENGINEER,
        controlMemType: tRatingLoad.CONTROL_MEM_TYPE,
        createdDateTime: tRatingLoad.CREATEDATETIME,
        createdUserId: tRatingLoad.CREATEUSERKEY,
        fatigLoadType: tRatingLoad.FATIG_LOAD_TYPE,
        fatigStressCat: tRatingLoad.FATIG_STRESS_CAT,
        invRatingStressMethod: tRatingLoad.INV_RATING_STRESS_METHOD,
        irLoad: tRatingLoad.IRLOAD,
        irLoadFactor: tRatingLoad.IR_LOAD_FACTOR,
        legalLoadRF: tRatingLoad.LEGAL_LOAD_RF,
        loadType: tRatingLoad.LOAD_TYPE,
        nbiRatingInd: tRatingLoad.NBI_RATING_IND,
        notes: tRatingLoad.NOTES,
        oprRatingStressMethod: tRatingLoad.OPR_RATING_STRESS_METHOD,
        orLoad: tRatingLoad.ORLOAD,
        orLoadFactor: tRatingLoad.OR_LOAD_FACTOR,
        ratingAnalysisMethod: tRatingLoad.RATING_ANALYSIS_METHOD,
        slcRating: tRatingLoad.SLC_RATING,
        stressRange: tRatingLoad.STRESS_RANGE,
        supportDataset: tRatingLoad.SUPPORT_DATASET,
        updateDateTime: tRatingLoad.MODTIME,
        updateUserId: tRatingLoad.USERKEY,
        vehPostWeight: tRatingLoad.VEH_POST_WEIGHT,
        vehPostTypes: vehPostTypes,
      });
    }

    structure.userInspection.recommendRatingReview = loadRatingValue.RECOMMEND_RATING_REVIEW;
    structure.userInspection.lrReviewAction = loadRatingValue.LR_REVIEW_ACTION;
    structure.userInspection.postReviewRec = loadRatingValue.POST_REVIEW_REC;
    structure.userInspection.ratingDate = loadRatingValue.RATING_DATE;
    structure.userInspection.ratingApprDate = loadRatingValue.RATING_APPR_DATE;
    structure.userInspection.approverName = loadRatingValue.APPROVER_NAME;
    structure.structureBridge.designLoad = loadRatingValue.DESIGNLOAD;
    structure.structureBridge.servTypOn = loadRatingValue.SERVTYPON;
    structure.userBridge.designMethod = loadRatingValue.DESIGN_METHOD;
    structure.structureBridge.routinePermitLoads = loadRatingValue.ROUTINE_PERMIT_LOADS;
    structure.postings = postings;
    structure.ratingLoads = ratingLoads;
    structure.tunnelLoadRating = loadRatingValue.tunnelLoadRating;
    structure.nbiAndAlternateRating = loadRatingValue.nbiAndAlternateRating;
  };

  const convertToInspModuleObject = (structure) => {
    let postings = [];
    for (const posting of structure?.postings ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of posting.vehPostTypes ?? []) {
        vehPostTypes.push({
          POSTING_ID: vehPostType.postingId,
          VEH_POST_TYPE: vehPostType.vehPostType,
        });
      }
      postings.push({
        POSTING_ID: posting.postingId,
        BRKEY: posting.brkey,
        POSTING_KEY: posting.postingKey,
        ACTIVE: posting.active,
        FIELD_COND: posting.fieldCond,
        IMPACT: posting.impact,
        PERMANENTLY_CLOSED_STRUC: posting.permanentlyClosedStruc,
        POST_LIMIT_COMB: posting.postLimitComb,
        POST_LIMIT_WEIGHT: posting.postLimitWeight,
        POST_REASON: posting.postReason,
        POST_STATUS: posting.postStatus,
        POST_STATUS_DATE: posting.postStatusDate,
        SNBI_POST_CONDITION: posting.snbiPostCondition,
        SNBI_POST_STATUS: posting.snbiPostStatus,
        SPEC_COND: posting.specCond,
        SPEC_RESTRICT_POST: posting.specRestrictPost,
        MODTIME: posting.updateDateTime,
        USERKEY: posting.updateUserId,
        VEH_POST_TYPES: vehPostTypes,
      });
    }

    let ratingLoads = [];
    for (const ratingLoad of structure?.ratingLoads ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of ratingLoad.vehPostTypes ?? []) {
        vehPostTypes.push({
          RATING_LOAD_ID: vehPostType.ratingLoadId,
          VEH_POST_TYPE: vehPostType.vehPostType,
        });
      }
      ratingLoads.push({
        RATING_LOAD_ID: ratingLoad.ratingLoadId,
        BRKEY: ratingLoad.brkey,
        RATING_DATE: ratingLoad.ratingDate,
        RATING_KEY: ratingLoad.ratingKey,
        AASHTO_MANUAL_YEAR: ratingLoad.aashtoManualYear,
        AASHTO_SPEC_YEAR: ratingLoad.aashtoSpecYear,
        ANALYSIS_ENGINEER: ratingLoad.analysisEngineer,
        CONTROL_MEM_TYPE: ratingLoad.controlMemType,
        CREATEDATETIME: ratingLoad.createdDateTime,
        CREATEUSERKEY: ratingLoad.createdUserId,
        FATIG_LOAD_TYPE: ratingLoad.fatigLoadType,
        FATIG_STRESS_CAT: ratingLoad.fatigStressCat,
        INV_RATING_STRESS_METHOD: ratingLoad.invRatingStressMethod,
        IRLOAD: ratingLoad.irLoad,
        IR_LOAD_FACTOR: ratingLoad.irLoadFactor,
        LEGAL_LOAD_RF: ratingLoad.legalLoadRF,
        LOAD_TYPE: ratingLoad.loadType,
        NBI_RATING_IND: ratingLoad.nbiRatingInd,
        NOTES: ratingLoad.notes,
        OPR_RATING_STRESS_METHOD: ratingLoad.oprRatingStressMethod,
        ORLOAD: ratingLoad.orLoad,
        OR_LOAD_FACTOR: ratingLoad.orLoadFactor,
        RATING_ANALYSIS_METHOD: ratingLoad.ratingAnalysisMethod,
        SLC_RATING: ratingLoad.slcRating,
        STRESS_RANGE: ratingLoad.stressRange,
        SUPPORT_DATASET: ratingLoad.supportDataset,
        MODTIME: ratingLoad.updateDateTime,
        USERKEY: ratingLoad.updateUserId,
        VEH_POST_WEIGHT: ratingLoad.vehPostWeight,
        VEH_POST_TYPES: vehPostTypes,
      });
    }

    loadRatingData.value = {
      RECOMMEND_RATING_REVIEW: structure.userInspection.recommendRatingReview,
      LR_REVIEW_ACTION: structure.userInspection.lrReviewAction,
      POST_REVIEW_REC: structure.userInspection.postReviewRec,
      RATING_DATE: structure.userInspection.ratingDate,
      RATING_APPR_DATE: structure.userInspection.ratingApprDate,
      APPROVER_NAME: structure.userInspection.approverName,
      DESIGNLOAD: structure.structureBridge.designLoad,
      SERVTYPON: structure.structureBridge.servTypOn,
      DESIGN_METHOD: structure.userBridge.designMethod,
      ROUTINE_PERMIT_LOADS: structure.structureBridge.routinePermitLoads,
      postings: postings,
      ratingLoads: ratingLoads,
      tunnelLoadRating: structure.tunnelLoadRating,
      nbiAndAlternateRating: structure.nbiAndAlternateRating,
    };
    calculationDates.value = [loadRatingData.value.RATING_DATE];
  };

  const convertToInspLinkObject = (loadRating) => {
    let postings = [];
    for (const posting of loadRating?.postings ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of posting.vehPostTypes ?? []) {
        vehPostTypes.push({
          POSTING_ID: vehPostType.postingId,
          VEH_POST_TYPE: vehPostType.vehPostType,
        });
      }
      postings.push({
        POSTING_ID: posting.postingId,
        BRKEY: posting.brkey,
        POSTING_KEY: posting.postingKey,
        ACTIVE: posting.active,
        FIELD_COND: posting.fieldCond,
        IMPACT: posting.impact,
        PERMANENTLY_CLOSED_STRUC: posting.permanentlyClosedStruc,
        POST_LIMIT_COMB: posting.postLimitComb,
        POST_LIMIT_WEIGHT: posting.postLimitWeight,
        POST_REASON: posting.postReason,
        POST_STATUS: posting.postStatus,
        POST_STATUS_DATE: posting.postStatusDate,
        SNBI_POST_CONDITION: posting.snbiPostCondition,
        SNBI_POST_STATUS: posting.snbiPostStatus,
        SPEC_COND: posting.specCond,
        SPEC_RESTRICT_POST: posting.specRestrictPost,
        MODTIME: posting.updateDateTime,
        USERKEY: posting.updateUserId,
        VEH_POST_TYPES: vehPostTypes,
      });
    }
    let ratingLoads = [];
    for (const ratingLoad of loadRating?.ratingLoads ?? []) {
      let vehPostTypes = [];
      for (const vehPostType of ratingLoad.vehPostTypes ?? []) {
        vehPostTypes.push({
          RATING_LOAD_ID: vehPostType.ratingLoadId,
          VEH_POST_TYPE: vehPostType.vehPostType,
        });
      }
      ratingLoads.push({
        RATING_LOAD_ID: ratingLoad.ratingLoadId,
        BRKEY: ratingLoad.brkey,
        RATING_DATE: ratingLoad.ratingDate,
        RATING_KEY: ratingLoad.ratingKey,
        AASHTO_MANUAL_YEAR: ratingLoad.aashtoManualYear,
        AASHTO_SPEC_YEAR: ratingLoad.aashtoSpecYear,
        ANALYSIS_ENGINEER: ratingLoad.analysisEngineer,
        CONTROL_MEM_TYPE: ratingLoad.controlMemType,
        CREATEDATETIME: ratingLoad.createdDateTime,
        CREATEUSERKEY: ratingLoad.createdUserId,
        FATIG_LOAD_TYPE: ratingLoad.fatigLoadType,
        FATIG_STRESS_CAT: ratingLoad.fatigStressCat,
        INV_RATING_STRESS_METHOD: ratingLoad.invRatingStressMethod,
        IRLOAD: ratingLoad.irLoad,
        IR_LOAD_FACTOR: ratingLoad.irLoadFactor,
        LEGAL_LOAD_RF: ratingLoad.legalLoadRF,
        LOAD_TYPE: ratingLoad.loadType,
        NBI_RATING_IND: ratingLoad.nbiRatingInd,
        NOTES: ratingLoad.notes,
        OPR_RATING_STRESS_METHOD: ratingLoad.oprRatingStressMethod,
        ORLOAD: ratingLoad.orLoad,
        OR_LOAD_FACTOR: ratingLoad.orLoadFactor,
        RATING_ANALYSIS_METHOD: ratingLoad.ratingAnalysisMethod,
        SLC_RATING: ratingLoad.slcRating,
        STRESS_RANGE: ratingLoad.stressRange,
        SUPPORT_DATASET: ratingLoad.supportDataset,
        MODTIME: ratingLoad.updateDateTime,
        USERKEY: ratingLoad.updateUserId,
        VEH_POST_WEIGHT: ratingLoad.vehPostWeight,
        VEH_POST_TYPES: vehPostTypes,
        deleted: ratingLoad.deleted,
      });
    }
    loadRatingData.value = {
      BRKEY: loadRating.brkey,
      INSPECTIONID: loadRating.inspectionId,
      RECOMMEND_RATING_REVIEW: loadRating.recommendRatingReview,
      LR_REVIEW_ACTION: loadRating.lrReviewAction,
      POST_REVIEW_REC: loadRating.postReviewRec,
      RATING_DATE: loadRating.calculationDate,
      RATING_APPR_DATE: loadRating.ratingApprDate,
      APPROVER_NAME: loadRating.approverName,
      DESIGNLOAD: loadRating.designLoad,
      SERVTYPON: loadRating.servTypOn,
      DESIGN_METHOD: loadRating.designMethod,
      ROUTINE_PERMIT_LOADS: loadRating.routinePermitLoads,
      postings: postings,
      ratingLoads: ratingLoads,
      tunnelLoadRating: loadRating.tunnelLoadRating,
      inspectionComments: loadRating.inspectionComments,
      nbiAndAlternateRating: loadRating.nbiAndAlternateRating,
      previousInspectionComments: loadRating.previousInspectionComments,
      assignedRatingDates: loadRating.assignedRatingDates,
    };
    calculationDates.value = loadRating.calculationDates;
  };

  const getVehiclePostTypes = (vehPostTypes) => {
    return vehPostTypes.map((vehPostType) => {
      return vehPostType["VEH_POST_TYPE"];
    });
  };

  const sortDetails = (arr) => {
    return arr?.sort(function (a, b) {
      return (
        convertToInt(a.LOAD_TYPE) - convertToInt(b.LOAD_TYPE) ||
        convertToInt(a.NBI_RATING_IND) - convertToInt(b.NBI_RATING_IND)
      );
    });
  };

  let getNewRatingKey = (loadRatingDetails) => {
    if (loadRatingDetails.length > 0) {
      return Math.max.apply(
        null,
        loadRatingDetails.map(function (ratingLoads) {
          return ratingLoads.RATING_KEY + 1;
        }),
      );
    } else {
      return 1;
    }
  };

  const validateLoadType = (item, loadRatingDetails) => {
    const ratingLoad = loadRatingDetails.find(
      (ratingLoad) =>
        item.RATING_KEY != ratingLoad.RATING_KEY &&
        ratingLoad.LOAD_TYPE == item.LOAD_TYPE &&
        ratingLoad.NBI_RATING_IND != "1",
    );
    if (item.NBI_RATING_IND == "1") {
      return true;
    } else if (ratingLoad) {
      return false;
    } else {
      return true;
    }
  };

  const convertToVehPostTypeObjects = (detail, vehPostTypes, isRating) => {
    let returnedVehPostTypes = [];
    for (const vehPostType of vehPostTypes ?? []) {
      if (isRating) {
        returnedVehPostTypes.push({
          RATING_LOAD_ID: detail.RATING_LOAD_ID,
          VEH_POST_TYPE: vehPostType,
        });
      } else {
        returnedVehPostTypes.push({
          POSTING_ID: detail.POSTING_ID,
          VEH_POST_TYPE: vehPostType,
        });
      }
    }
    return returnedVehPostTypes;
  };

  const getRating = (item, ratingType) => {
    const loadFactor = ratingType === "IR" ? item.IR_LOAD_FACTOR : item.OR_LOAD_FACTOR;

    if (loadFactor >= 0 && loadFactor <= 3 && ["1", "2", "8", "0"].includes(item.LOAD_TYPE)) {
      switch (item.LOAD_TYPE) {
        case "1":
          return (
            loadFactor *
            configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "H20")?.split("-")[1]
          );
        case "2":
          return (
            loadFactor *
            configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "HS20")?.split("-")[1]
          );
        case "8":
          return (
            loadFactor *
            configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "ML80")?.split("-")[1]
          );
        case "0":
          return (
            loadFactor *
            configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "TK527")?.split("-")[1]
          );
      }
    }
    return null;
  };

  const inventoryAndOperatingRatingConditionalCheck = (item) => {
    return (
      ["8", "D", "E", "F"].includes(item.RATING_ANALYSIS_METHOD) ||
      (item.RATING_ANALYSIS_METHOD == "6" && item.LOAD_TYPE == "D")
    );
  };

  const vehiclePostTypesDisplayValue = (vehPostTypes) => {
    return vehPostTypes
      ?.filter((v) => v?.length > 0)
      ?.map((v) => configStore.getReferenceValue(REFERENCE_TABLE.VEHICLE_POSTING_TYPE, v))
      ?.join();
  };

  const updateLegalLoadRatingFactor = (item) => {
    if (inventoryAndOperatingRatingConditionalCheck(item)) {
      item.LEGAL_LOAD_RF = item.OR_LOAD_FACTOR;
    } else {
      switch (item.LOAD_TYPE) {
        case "1":
        case "A":
          item.LEGAL_LOAD_RF = Number.parseFloat(
            item.ORLOAD / configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "H20")?.split("-")[1],
          )?.toFixed(2);
          break;
        case "2":
        case "B":
          item.LEGAL_LOAD_RF = Number.parseFloat(
            item.ORLOAD / configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "HS20")?.split("-")[1],
          )?.toFixed(2);
          break;
        case "8":
        case "C":
          item.LEGAL_LOAD_RF = Number.parseFloat(
            item.ORLOAD / configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "ML80")?.split("-")[1],
          )?.toFixed(2);
          break;
        case "0":
          item.LEGAL_LOAD_RF = Number.parseFloat(
            item.ORLOAD / configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "TK527")?.split("-")[1],
          )?.toFixed(2);
          break;
        case "E":
          item.LEGAL_LOAD_RF = Number.parseFloat(
            item.ORLOAD / configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "EV-2")?.split("-")[2],
          )?.toFixed(2);
          break;
        case "F":
          item.LEGAL_LOAD_RF = Number.parseFloat(
            item.ORLOAD / configStore.getReferenceValue(REFERENCE_TABLE.LEGAL_LOAD_RATING_FACTOR_WEIGHT, "EV-3")?.split("-")[2],
          )?.toFixed(2);
          break;
        default:
          item.LEGAL_LOAD_RF = null;
          break;
      }
    }
  };

  return {
    vehiclePostTypesDisplayValue,
    getRating,
    inventoryAndOperatingRatingConditionalCheck,
    convertToVehPostTypeObjects,
    validateLoadType,
    getNewRatingKey,
    sortDetails,
    getVehiclePostTypes,
    getCalculationDates,
    setCalculationDates,
    getLoadRatingObject,
    saveLoadRating,
    loadLoadRatingObject,
    updateLegalLoadRatingFactor,
  };
});
